import React from 'react'
import styled from 'styled-components'
import { Login, Pricing, SignUp } from '../../pages/imports'
import Icon from '../../components/general/Icon'
import PropTypes from 'prop-types'
import translate from '../../helper/translate'
import Link from '../../components/router/Link'
import { desktop, VIEWPORT_DESKTOP, VIEWPORT_MOBILE } from '../../styles/media'
import { connect } from 'react-redux'
import { bindActionCreators, compose } from 'redux'
import references from '../../data/references'
import { setReference } from '../../state/actions/app'
import { install } from '../../helper/pwa'
import { getCollectedCardsCount } from '../../helper/collection'
import AnimatedNumber from '../../components/animation/AnimatedNumber'

const SignUpButtons = ({ authToken, collections, pwaInstalled, pwaInstallEvent, setReference, subscribed, t, transparent, viewport }) => {
	const canInstall = pwaInstallEvent && viewport === VIEWPORT_MOBILE

	return (
		<>
			{!authToken && (
				<>
					<SiteHeaderButton
						login
						to="/login"
						onMouseOver={() => Login.preload()}
						transparent={transparent}
					>
						<Icon name="key" />
						<span>{t('general.login')}</span>
					</SiteHeaderButton>
					<SiteHeaderButton
						primary
						to="/signup"
						onMouseOver={() => SignUp.preload()}
						transparent={transparent}
					>
						<Icon name="user-circle" />
						<span>{t('general.register')}</span>
					</SiteHeaderButton>
				</>
			)}
			{authToken && (subscribed || pwaInstalled) && (
				<SiteHeaderButtonWithCounter transparent={transparent}>
					<SiteHeaderButtonWithCounterLeft
						to="/mycollections"
						transparent={transparent}
						title={t('general.collectionManager')}
					>
						<strong>
							<AnimatedNumber>
								{getCollectedCardsCount(collections).toLocaleString()}
							</AnimatedNumber>
						</strong>
						<span>
							{t('general.collectedCards')}
						</span>
					</SiteHeaderButtonWithCounterLeft>
					<SiteHeaderButton
						noTransform
						to="/add"
						transparent={transparent}
						title={t('general.addCards')}
					>
						<Icon name="document-add" />
					</SiteHeaderButton>
				</SiteHeaderButtonWithCounter>
			)}
			{authToken && !pwaInstalled && (
				<SiteHeaderButton
					secondary
					to={!canInstall ? '/install' : null}
					onClick={canInstall ? () => install(pwaInstallEvent) : undefined}
					transparent={transparent}
				>
					<Icon name="device-mobile" />
					<span>{t('general.install')}</span>
				</SiteHeaderButton>
			)}
			{authToken && !subscribed && viewport === VIEWPORT_DESKTOP && (
				<SiteHeaderButton
					to="/pricing"
					onBeforeNavigate={() => setReference(references.MENU_PRO_BUTTON)}
					onMouseOver={() => Pricing.preload()}
					transparent={transparent}
				>
					<Icon name="star" />
					<span><strong>PRO</strong></span>
				</SiteHeaderButton>
			)}
		</>
	)
}

SignUpButtons.propTypes = {
	authToken: PropTypes.string,
	collections: PropTypes.array,
	pwaInstalled: PropTypes.bool,
	pwaInstallEvent: PropTypes.any,
	setReference: PropTypes.func.isRequired,
	subscribed: PropTypes.string,
	t: PropTypes.func.isRequired,
	transparent: PropTypes.string,
	viewport: PropTypes.string.isRequired
}

SignUpButtons.defaultProps = {
	authToken: null,
	collections: [],
	pwaInstalled: false,
	pwaInstallEvent: null,
	subscribed: null,
	transparent: null
}

const SiteHeaderButton = styled(Link)`
	background: ${props => props.theme.primary};
	box-shadow: ${props => props.theme.shadowSearch};
	border-radius: 8px;
	color: #FFF;
	font-weight: 500;
	height: 40px;
	padding: 0 1rem;
	position: relative;
	text-decoration: none;
	transition: 0.2s;

	align-items: center;
	display: flex;

	svg {
		height: 20px;
		width: 20px;
	}

	> span {
		margin-left: 0.5rem;
		white-space: nowrap;
	}

	strong {
		font-size: 1.1rem;
	}

	${props => props.secondary ? `
		background: ${props.transparent === 'dark' ? (props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.5)') : props.theme.secondary};

		&, svg {
			color: ${props.theme.white};
		}
	` : ''}

	${desktop`
		&:hover {
			background: ${props => props.theme.secondaryHover};
			color: ${props => props.theme.white};
			text-decoration: none;
			box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.3);

			${props => !props.secondary && `
				background: ${props.theme.primaryHover};
			`}

			${props => !props.noTransform && `
				transform: translateY(-3px);
			`}
		}
	`}

	${props => props.login && `
		background: transparent;
		box-shadow: none;
		color: ${props.theme.textLight};

		${props.transparent ? `
			color: ${props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
		` : ''}

		&:hover {
			background: rgba(0, 0, 0, 0.1);
			box-shadow: none;
			color: ${props.theme.text};
			transform: none;

			${props.transparent && `
				color: ${props.transparent === 'dark' ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 1)'};
			`}
		}
	`}
`

const SiteHeaderButtonWithCounter = styled.div`
	align-items: center;
	display: flex;
	height: 40px;
	width: fit-content;

	background: ${props => props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.1)' : props.theme.background};
	border-radius: 4px 8px 8px 4px;
	box-shadow: 0 0 0 2px ${props => props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.2)' : props.theme.backgroundLight};
	transition: 0.1s;

	${desktop`
		&:hover {
			box-shadow: 0 0 0 2px ${props => props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.3)' : (props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.1)' : props.theme.backgroundConcrete)};
		}
	`}
`

const SiteHeaderButtonWithCounterLeft = styled(Link)`
	align-items: center;
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
	padding: 0.25rem 0.5rem;
	text-decoration: none;

	> strong {
		color: ${props => props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.9)' : props.theme.text};
		font-size: 1.1rem;
		font-weight: 700;
	}

	> span {
		color: ${props => props.transparent === 'dark' ? 'rgba(255, 255, 255, 0.7)' : props.theme.textLight};
		font-size: 0.6rem;
		font-weight: 600;
		text-transform: uppercase;
		white-space: nowrap;
	}
`

const enhance = compose(
	connect(state => ({
		authToken: state.persist.authToken,
		collections: state.persist.collections,
		pwaInstalled: state.persist.user.pwaInstalled,
		pwaInstallEvent: state.app.pwaInstallEvent,
		subscribed: state.persist.user.subscribed,
		viewport: state.app.viewport
	}), dispatch => bindActionCreators({
		setReference
	}, dispatch)),
	translate('general')
)

export default enhance(SignUpButtons)
